import { useState, useLayoutEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import {
  CurrentUser,
  CustomerGearResponse,
  CustomerProfile,
  GearFilters,
  MenuResponse,
  ModalProps,
  OgData,
  SimpleItem,
} from 'types';
import { blockUser, getCustomerProfile, postCustomerData } from 'api';
import {
  toAuthAndBack,
  goToComment,
  handleFavorite,
  isParticularGearOwner,
  openMobileApp,
  checkIfFullyAuthorized,
} from 'utils/services';
import { Modal } from 'components/Modal';
import { Snackbar, SnackbarProps } from 'components/Snackbar';
import { CustomerPageWrapper } from 'containers/CustomerPageWrapper';
import { ErrorPage } from 'containers/ErrorPage';
import { ShareModal } from 'containers/MyGear/ShareModal';
import { ReportModal } from './ReportModal';
import { UserProfileHeader } from './UserProfileHeader';
import tabsMap, { tabs, TabType } from './tabsMap';

import styles from './UserProfile.module.scss';
import { composeServerErrorForSnackbar } from '../../utils/formatters';
import {
  USER_BACKGROUND_PLACEHOLDER_LINK,
  USER_PHOTO_PLACEHOLDER_LINK,
  USER_PHOTO_PLACEHOLDER_MONOCHROME_LINK,
} from '../../consts';
import Image from 'next/legacy/image';
import { getPageH1Title } from '../../utils/seoFormatters';

export type UserProfilePageProps = {
  filters: GearFilters;
  listingData: CustomerGearResponse;
  menuData: MenuResponse;
  ogData?: OgData[];
  profile?: CustomerProfile;
  reportReasons?: Array<SimpleItem>;
  user?: CurrentUser;
  tab?: TabType;
  username: string;
};

export const UserProfilePage = ({
  filters,
  listingData,
  menuData,
  profile,
  ogData,
  reportReasons,
  user,
  username,
  tab,
}: UserProfilePageProps) => {
  const router = useRouter();
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | undefined>(profile);
  const [activeTab, setActiveTab] = useState<TabType>(tab || tabs[0].id);
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<SnackbarProps | null>(null);
  const [modalProps, setModalProps] = useState<ModalProps | null>(null);
  const isFullyAuth = checkIfFullyAuthorized(user);

  useLayoutEffect(() => {
    openMobileApp(`customers/${username}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateData = async () => {
    if (customerProfile) {
      const response = await getCustomerProfile(customerProfile.username);
      if (response.ok) {
        setCustomerProfile(response.data);
        setSnackbarMessage({ header: 'Profile updated successfully' });
      }
    }
  };

  const handleChangeTab = (tab: TabType) => {
    setActiveTab(tab);
  };

  const handleShareModalOpen = (slug: string, owner: Partial<CustomerProfile>, isSold: boolean) => {
    setModalProps({
      child: (
        <ShareModal
          dividerText="or"
          isGearOwner={isParticularGearOwner(owner, user)}
          isSold={isSold}
          slug={slug}
          shareToFollowersBtn
          user={user}
        />
      ),
      title: 'Share this gear',
      onCoverClick: () => {
        setModalProps(null);
      },
      withCrossIcon: true,
    });
  };

  const redirectToAuth = () => toAuthAndBack(`users/${username}`, router, user);

  const toggleFavorite = (slug: string, isFavorite: boolean) => handleFavorite(slug, isFavorite, setSnackbarMessage);

  const onFollow = async () => {
    const result = await postCustomerData(customerProfile?.username || '', 'follow');
    if (result.ok) {
      handleUpdateData().then();
      return true;
    } else {
      setSnackbarMessage({header: result.message || result.error || 'Failed to follow user', mode: 'error'})
      return false;
    }
  };

  const onBlock = useCallback((block) => {
    if(customerProfile?.username)
      blockUser(customerProfile.username, {block})
        .then((resp) => {
          setSnackbarMessage({
            mode: 'success',
            header: `${customerProfile.username} is ${block ? '' : 'un'}blocked`
          });
          setCustomerProfile({...customerProfile, is_blacklisted: block})
        })
        .catch((err) => {
          setSnackbarMessage(composeServerErrorForSnackbar(err.response));
        })
  }, [customerProfile])

  return !customerProfile ? (
    <ErrorPage statusCode={404} errorMessage="User Not Found" menuData={menuData} user={user} />
  ) : (
    <CustomerPageWrapper
      menuData={menuData}
      user={user}
      ogData={ogData}
      headTitle={`${customerProfile.username} | SENDY`}
      hiddenComponentProps={{ title: getPageH1Title('user', { username: customerProfile.username }) }}
    >
      <div className={styles.container}>
        <UserProfileHeader
          user={user}
          profile={{...customerProfile, listings: listingData.total}}
          activeTab={activeTab}
          onChangeTab={handleChangeTab}
          onFollow={isFullyAuth ? onFollow : redirectToAuth}
          onSendReport={isFullyAuth ? () => setShowReportModal(true) : redirectToAuth}
          onBlock={isFullyAuth ? onBlock : redirectToAuth}
        />

        <div className={styles.tabContentContainer}>

          {customerProfile.in_blacklist ? (
            <div className={styles.blockedContainer}>
              <Image
                src={USER_PHOTO_PLACEHOLDER_MONOCHROME_LINK}
                alt="cover image"
                width={120}
                height={120}
              />
              <p className={styles.title}>You cannot see Profile</p>
              <p className={styles.description}>It is not allowed since {customerProfile.full_name} blocked you</p>
            </div>
          ) : (tabsMap[activeTab]?.tab({
            user,
            profile: customerProfile,
            username,
            setSnackbarMessage,
            toggleFavorite: isFullyAuth ? toggleFavorite : redirectToAuth,
            onShare: isFullyAuth ? handleShareModalOpen : redirectToAuth,
            onComment: isFullyAuth ? goToComment : redirectToAuth,
            listingData,
            filters,
            setActiveTab,
          }))}
        </div>

        {showReportModal && (
          <ReportModal
            reportReasons={reportReasons || []}
            profile={customerProfile}
            onClose={() => setShowReportModal(false)}
            onSetSnackbarMessage={(message) => setSnackbarMessage(message)}
          />
        )}

        {snackbarMessage && <Snackbar {...snackbarMessage} onClose={() => setSnackbarMessage(null)} />}
        {modalProps && <Modal {...modalProps} />}
      </div>
    </CustomerPageWrapper>
  );
};
